import { createContext, useContext } from 'react';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';

export interface TodoListContextValue {
  // Data
  /**
   * Whether the current user can MANAGE this list — create / delete / reorder /
   * assign, and edit any item or section. True for the owner of a Personal list and
   * for owner/admin members of a shared (Org) workspace. A plain member is NOT a
   * manager; they may only edit items / sections assigned to them (compared against
   * currentPrincipalId). The server still enforces the precise Vault role on write.
   */
  isOwner: boolean;
  currentUserId: string | undefined;
  /** The current user's Vault Principal ID, for "assigned to me" edit checks. */
  currentPrincipalId: string | undefined;
  visibleSectionIds: Set<string> | undefined;
  /** Whether this list requires items to pass an "In Review" step before they
   * can be completed. Drives the checkbox semantics and the Verify/Send-back
   * controls in {@link TodoItemRow}. Absent/false = classic behavior. */
  reviewMode?: boolean;

  // Callbacks (optional — not provided for public/read-only views)
  onToggleItem?: (item: TodoItemResponse) => void;
  /** Approve a review item (in_review → completed). Only meaningful in review mode. */
  onApproveItem?: (item: TodoItemResponse) => void;
  /** Send a review item back to active work (in_review → in_progress). */
  onRejectItem?: (item: TodoItemResponse) => void;
  onEditItem?: (item: TodoItemResponse) => void;
  onDeleteItem?: (itemId: string) => void;
  onEditSection?: (section: TodoSectionResponse) => void;
  onDeleteSection?: (sectionId: string) => void;
  onCreateItem?: (input: { title: string; description?: string; sectionId?: string }) => void;
  onCreateSection?: (input: {
    name: string;
    description?: string;
    parentSectionId?: string;
    sectionType?: 'timeframe' | 'category';
  }) => void;
  onReorderItems?: (sectionId: string | null, itemIds: string[]) => void;
  onReorderSections?: (sectionIds: string[]) => void;
  onUpdateItemTitle?: (itemId: string, title: string) => void;
  onCreateItemBelow?: (
    sectionId: string | null,
    afterItemId: string,
    title: string,
  ) => Promise<void>;
  onToggleHiddenFromPublic?: (sectionId: string, hidden: boolean) => void;

  // Item / section assignment (shared/Org workspaces only; manager-only)
  onAssignItem?: (itemId: string, assigneePrincipalId: string) => void;
  onUnassignItem?: (itemId: string) => void;
  onAssignSection?: (sectionId: string, assigneePrincipalId: string) => void;
  onUnassignSection?: (sectionId: string) => void;
  /**
   * Active members of the current workspace that an item or section can be assigned
   * to. Empty for Personal Holders.
   */
  assignableMembers?: Array<{ principalId: string; label: string }>;
  /** Resolve a principalId to a display name, for the "assigned to" badge. */
  resolvePrincipalName?: (principalId: string | null | undefined) => string;
}

const TodoListContext = createContext<TodoListContextValue | null>(null);

export function TodoListProvider({
  value,
  children,
}: {
  value: TodoListContextValue;
  children: React.ReactNode;
}) {
  return <TodoListContext.Provider value={value}>{children}</TodoListContext.Provider>;
}

export function useTodoListContext(): TodoListContextValue {
  const ctx = useContext(TodoListContext);
  if (!ctx) {
    throw new Error('useTodoListContext must be used within a TodoListProvider');
  }
  return ctx;
}
